import { supabase } from './supabaseClient';

export const NEGOCIO_MAX_FOTOS = 5;
export const NEGOCIO_BUCKET = 'negocios-fotos';

export const NEGOCIO_TIPO_TO_UI = {
  negocio: 'Negocio',
  servicio: 'Servicio',
};

export const NEGOCIO_STATUS_TO_UI = {
  pending: 'Pendiente',
  approved: 'Aprobado',
  flagged: 'Reportado',
  rejected: 'Rechazado',
};

export const CONTACTO_TIPO_TO_UI = {
  whatsapp: 'WhatsApp',
  link: 'Link',
  ninguno: 'Ninguno',
};

export function negocioTipoToUi(tipo) {
  return NEGOCIO_TIPO_TO_UI[tipo] || tipo;
}

export function negocioStatusToUi(status) {
  return NEGOCIO_STATUS_TO_UI[status] || status;
}

/** Normalize a name into a URL-safe slug base */
export function slugifyNegocioNombre(nombre) {
  const base = String(nombre || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return base || 'negocio';
}

/** slugify + short random suffix to reduce collisions */
export function makeNegocioSlug(nombre) {
  const suffix = Math.random().toString(36).slice(2, 7);
  return `${slugifyNegocioNombre(nombre)}-${suffix}`;
}

export function mapNegocioFromDb(row) {
  if (!row) return null;
  return {
    ...row,
    fotos: Array.isArray(row.fotos) ? row.fotos : [],
    items: Array.isArray(row.items) ? row.items : [],
    contacto_tipo: row.contacto_tipo || 'ninguno',
  };
}

/** Public URL for a storage path (or passthrough if already absolute) */
export function negocioFotoPublicUrl(pathOrUrl) {
  if (!pathOrUrl) return null;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const { data } = supabase.storage.from(NEGOCIO_BUCKET).getPublicUrl(pathOrUrl);
  return data?.publicUrl || null;
}

/**
 * Upload a file under {listingId}/{kind}-{timestamp}.{ext}
 * Returns the storage path (not the public URL).
 */
export async function uploadNegocioFoto(listingId, file, kind = 'foto') {
  if (!listingId) throw new Error('Falta el id del listing para subir la foto');
  if (!file) throw new Error('No hay archivo para subir');

  const ext = (file.name?.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const safeKind = String(kind || 'foto').replace(/[^a-z0-9_-]/gi, '');
  const path = `${listingId}/${safeKind}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}.${ext}`;

  const { error } = await supabase.storage.from(NEGOCIO_BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw error;
  return path;
}

export function buildNegocioInsertPayload({ memberId, form, slug }) {
  return {
    member_id: memberId,
    tipo: form.tipo,
    nombre: form.nombre.trim(),
    categoria: form.categoria?.trim() || null,
    descripcion: form.descripcion?.trim() || null,
    ubicacion: form.ubicacion?.trim() || null,
    fotos: [],
    logo_url: null,
    items: form.tipo === 'negocio' ? normalizeItems(form.items) : [],
    contacto_tipo: form.contacto_tipo || 'ninguno',
    contacto_valor:
      form.contacto_tipo && form.contacto_tipo !== 'ninguno'
        ? (form.contacto_valor?.trim() || null)
        : null,
    slug,
    status: 'pending',
  };
}

export function buildNegocioUpdatePayload(form) {
  return {
    tipo: form.tipo,
    nombre: form.nombre.trim(),
    categoria: form.categoria?.trim() || null,
    descripcion: form.descripcion?.trim() || null,
    ubicacion: form.ubicacion?.trim() || null,
    items: form.tipo === 'negocio' ? normalizeItems(form.items) : [],
    contacto_tipo: form.contacto_tipo || 'ninguno',
    contacto_valor:
      form.contacto_tipo && form.contacto_tipo !== 'ninguno'
        ? (form.contacto_valor?.trim() || null)
        : null,
  };
}

function normalizeItems(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => ({
      nombre: String(item.nombre || '').trim(),
      descripcion: String(item.descripcion || '').trim(),
      precio: item.precio != null && String(item.precio).trim() !== '' ? String(item.precio).trim() : undefined,
      foto_url: item.foto_url || undefined,
    }))
    .filter((item) => item.nombre);
}

export function emptyNegocioForm() {
  return {
    tipo: 'negocio',
    nombre: '',
    categoria: '',
    descripcion: '',
    ubicacion: '',
    contacto_tipo: 'ninguno',
    contacto_valor: '',
    items: [],
  };
}

export function negocioFormFromListing(listing) {
  if (!listing) return emptyNegocioForm();
  return {
    tipo: listing.tipo || 'negocio',
    nombre: listing.nombre || '',
    categoria: listing.categoria || '',
    descripcion: listing.descripcion || '',
    ubicacion: listing.ubicacion || '',
    contacto_tipo: listing.contacto_tipo || 'ninguno',
    contacto_valor: listing.contacto_valor || '',
    items: Array.isArray(listing.items)
      ? listing.items.map((item) => ({
          nombre: item.nombre || '',
          descripcion: item.descripcion || '',
          precio: item.precio || '',
          foto_url: item.foto_url || null,
          _existingFoto: item.foto_url || null,
        }))
      : [],
  };
}

export function contactoHref(contactoTipo, contactoValor) {
  if (!contactoValor) return null;
  if (contactoTipo === 'whatsapp') {
    const digits = String(contactoValor).replace(/[^\d]/g, '');
    return digits ? `https://wa.me/${digits}` : null;
  }
  if (contactoTipo === 'link') {
    const raw = String(contactoValor).trim();
    if (!raw) return null;
    return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  }
  return null;
}
